import { useEffect, useRef } from 'react';

/**
 * A custom hook to add silent pull-to-refresh functionality.
 * Prevents the native browser refresh UI to keep it "invisible".
 * 
 * @param {Function} onRefresh - The async function to call when refresh is triggered.
 * @param {number} threshold - The pixel distance required to trigger (default 100px).
 * @returns {React.RefObject} - A ref to attach to the scrollable container.
 */
export const usePullToRefresh = (onRefresh, threshold = 100) => {
  const containerRef = useRef(null);
  const touchStartY = useRef(0);
  const isDragging = useRef(false);
  const isLoading = useRef(false); // Prevent multiple triggers

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Pages scroll inside their own root (h-full + overflow-y-auto), so the
    // element that actually scrolls may be the container itself or one of
    // its ancestors. Find the nearest ancestor that can really scroll.
    const findScroller = (el) => {
      let node = el;
      while (node && node !== document.body && node !== document.documentElement) {
        const oy = getComputedStyle(node).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
          return node;
        }
        node = node.parentElement;
      }
      return document.scrollingElement || el;
    };

    const handleTouchStart = (e) => {
      // Only track if scrolled to top and not already loading
      if (findScroller(container).scrollTop <= 0 && !isLoading.current) {
        touchStartY.current = e.touches[0].clientY;
        isDragging.current = true;
      } else {
        isDragging.current = false;
      }
    };

    const handleTouchMove = (e) => {
      if (!isDragging.current) return;
      
      const currentY = e.touches[0].clientY;
      const distance = currentY - touchStartY.current;

      // If pulling down past 0
      if (distance > 0) {
        // Prevent native browser pull-to-refresh UI
        e.preventDefault(); 
      }
    };

    const handleTouchEnd = (e) => {
      if (!isDragging.current) return;

      const touchEndY = e.changedTouches[0].clientY;
      const distance = touchEndY - touchStartY.current;

      // Trigger refresh if past threshold
      if (distance > threshold && !isLoading.current) {
        isLoading.current = true; // Lock it
        if (typeof onRefresh === 'function') {
          // Allow the async function to run, then unlock
          Promise.resolve(onRefresh()).finally(() => {
            // Small delay to prevent accidental double triggers
            setTimeout(() => { isLoading.current = false; }, 500);
          });
        } else {
          isLoading.current = false;
        }
      }
      
      isDragging.current = false;
    };

    // passive: false is REQUIRED to call preventDefault() inside touchmove
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onRefresh, threshold]);

  return containerRef;
};